import type { ReactNode } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

interface EncryptionScreenProps {
  icon?: ReactNode;
  title: string;
  children: ReactNode;
}

/**
 * Layout for blocking encryption screens (recovery setup / unlock).
 * On mobile it presents as a non-dismissible bottom Sheet; on desktop as a
 * centered card. Content is left-aligned. The Sheet's close affordances are
 * disabled because these are gates the user must complete.
 */
const EncryptionScreen = ({ icon, title, children }: EncryptionScreenProps) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Sheet open>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl max-h-[92vh] overflow-y-auto text-left [&>button]:hidden"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <SheetHeader className="text-left space-y-2">
            {icon}
            <SheetTitle>{title}</SheetTitle>
          </SheetHeader>
          <div className="space-y-5 pt-3">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md dark:border-primary/20">
        <CardHeader className="text-left">
          {icon}
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">{children}</CardContent>
      </Card>
    </div>
  );
};

export default EncryptionScreen;
